import { ExternalLink, RefreshCw } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
} from '@/components/ui'
import { odooUrl } from '@/lib/utils/odoo-url'
import { useVendorAlternates } from '../queries'

/**
 * Sprint 11 Chunk 3 — vendor sourcing panel.
 *
 * Shared component co-located with the vendor-alternates-resolver
 * service (mirrors the cofa-registry pattern of co-locating
 * `CofaPreviewDrawer` with the registry queries). Consumed by
 * Formulations (Chunk 4) and Projects (Chunk 5).
 *
 * Server-side ranking applies Sprint 11 D-2 (approved-first →
 * cheapest → shortest lead) so this component just renders rows
 * in the order they arrive.
 *
 * Empty states:
 *  - No alternates at all → "No vendors on file for this product."
 *  - Alternates exist but none approved → still rendered;
 *    individual rows carry no "approved" chip. The header summary
 *    counts both flavors so the user knows whether they're seeing
 *    a regulated or unregulated set.
 */

export interface SourcingPanelProps {
  productId: number | null
  /** Optional product name for the header; falls back to "this product". */
  productLabel?: string | null
}

function formatCurrency(value: number | null): string {
  if (value === null) return '—'
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  })
}

function formatQty(value: number | null): string {
  if (value === null) return '—'
  return value.toLocaleString()
}

function formatLeadTime(days: number | null): string {
  if (days === null) return '—'
  if (days === 0) return 'in stock'
  return `${days} day${days === 1 ? '' : 's'}`
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso)
  const now = new Date()
  const ms = now.getTime() - then.getTime()
  if (Number.isNaN(ms) || ms < 0) {
    return then.toLocaleDateString()
  }
  const days = Math.floor(ms / (1000 * 60 * 60 * 24))
  if (days < 1) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 30) {
    const weeks = Math.floor(days / 7)
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`
  }
  if (days < 365) {
    const months = Math.floor(days / 30)
    return `${months} month${months === 1 ? '' : 's'} ago`
  }
  return then.toLocaleDateString()
}

export function SourcingPanel({
  productId,
  productLabel,
}: SourcingPanelProps) {
  const { data, isLoading, isFetching, isError, error, refetch } =
    useVendorAlternates(productId)

  const alternates = data ?? []
  const approvedCount = alternates.filter((a) => a.approved).length
  const totalCount = alternates.length

  if (productId === null || productId <= 0) {
    return (
      <div className="p-4">
        <EmptyState
          variant="no-data"
          title="Pick a product first"
          description="Sourcing alternates load once the ingredient row has a linked Odoo product."
        />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center justify-between gap-3 border-b border-border-subtle bg-surface-sunk px-4 py-2.5">
        <div className="flex flex-col gap-0.5">
          <span className="text-role-section-label">Sourcing</span>
          <span className="text-role-data-secondary">
            {productLabel ?? 'this product'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {totalCount > 0 ? (
            <span className="text-role-metadata">
              {approvedCount} approved · {totalCount} total
            </span>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Refresh sourcing data"
            loading={isFetching && !isLoading}
            onClick={() => refetch()}
          >
            <RefreshCw size={14} />
          </Button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            <div className="h-12 animate-pulse rounded-sm bg-surface-sunk" />
            <div className="h-12 animate-pulse rounded-sm bg-surface-sunk" />
            <div className="h-12 animate-pulse rounded-sm bg-surface-sunk" />
          </div>
        ) : isError ? (
          <div className="p-4">
            <EmptyState
              variant="error"
              title="Couldn't load sourcing data"
              description={
                error instanceof Error
                  ? error.message
                  : 'The /api/sourcing/alternates endpoint returned an error. Try Refresh; if it persists, check that the middleware container is up.'
              }
            />
          </div>
        ) : totalCount === 0 ? (
          <div className="p-4">
            <EmptyState
              variant="no-data"
              title="No vendors on file"
              description="No `product.supplierinfo` records exist for this product. Add one in the Vendor Pricelists module or in Odoo's product master."
            />
          </div>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {alternates.map((alt) => (
              <li
                key={alt.partner_id}
                className="flex flex-col gap-1 px-4 py-3 hover:bg-surface-hover"
              >
                <div className="flex items-center gap-2">
                  <a
                    href={odooUrl('res.partner', alt.partner_id)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-role-data-primary text-link hover:underline"
                  >
                    {alt.partner_name}
                    <ExternalLink size={10} />
                  </a>
                  {alt.approved ? (
                    <Chip size="xs" variant="success" dot>
                      Approved
                    </Chip>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-role-metadata">
                  <span>
                    <span className="text-tertiary">Price: </span>
                    <span className="text-secondary">{formatCurrency(alt.fixed_price)}</span>
                  </span>
                  <span>
                    <span className="text-tertiary">Min: </span>
                    <span className="text-secondary">{formatQty(alt.min_quantity)}</span>
                  </span>
                  <span>
                    <span className="text-tertiary">Lead: </span>
                    <span className="text-secondary">{formatLeadTime(alt.lead_time_days)}</span>
                  </span>
                  {alt.last_po_date ? (
                    <span>
                      <span className="text-tertiary">Last PO: </span>
                      <span className="text-secondary">
                        {formatRelativeDate(alt.last_po_date)}
                      </span>
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
